import { money } from "./unitStats";

const day = (v) => {
  if (!v) return "—";
  const d = new Date(String(v).slice(0, 10));
  if (!Number.isFinite(d.getTime())) return String(v);
  return d.toLocaleDateString("en-CA", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });
};

const place = (u) => (u.building_name && u.building_name !== u.property_name ? `${u.property_name} · ${u.building_name}` : u.property_name);

/** "Amy Renter, Bo" from Oct 1 · $1,450 */
const Incoming = ({ lease }) => {
  if (!lease) return <span className="avp-muted">Signed lease</span>;
  const names = lease.tenants?.length ? lease.tenants.join(", ") : "Signed lease";
  return (
    <>
      <span className="avp-strong">{names}</span>
      <span className="avp-muted"> from {day(lease.start_date)}</span>
    </>
  );
};

/** New rent against target: green at/above, amber below. */
const RentVsTarget = ({ rent, target }) => {
  if (rent === null || rent === undefined) return <span className="avp-muted">—</span>;
  if (!target) return money(rent);
  const diff = rent - target;
  return (
    <>
      {money(rent)}
      <span className={diff >= 0 ? "avp-up" : "avp-down"}> {diff >= 0 ? "+" : "−"}{money(Math.abs(diff))}</span>
    </>
  );
};

/**
 * The lease lined up after the current one: a signed one counts as rented, one still out
 * for signatures is shown but not counted. A renewal (same resident) is tagged as such.
 */
const NextLease = ({ next, none = <span className="avp-muted">Not rented yet</span> }) => {
  if (!next) return none;
  const tag = next.renewal ? <span className={`avp-tag ${next.signed ? "avp-tag--renewed" : "avp-tag--pending"}`}>Renewal</span> : null;
  if (next.signed) {
    return (
      <>
        <span className="avp-up">✓ </span>
        {tag}{tag && " "}
        <Incoming lease={next.lease} />
      </>
    );
  }
  return (
    <>
      {tag}{tag && " "}
      <Incoming lease={next.lease} />
      <span className="avp-down"> · awaiting signatures</span>
    </>
  );
};

/** "Nov 1, 2026 → Oct 31, 2027" */
const Term = ({ lease }) => (lease?.start_date ? <>{day(lease.start_date)}{lease.end_date ? ` → ${day(lease.end_date)}` : ""}</> : <span className="avp-muted">—</span>);

const Now = ({ u }) =>
  u.where === "VACANT" ? <span className="avp-tag avp-tag--vacant">Vacant now</span> : (
    <span>
      <span className="avp-strong">{u.tenant}</span>
      <span className="avp-muted"> ends {day(u.lease_end)}</span>
    </span>
  );

/**
 * The detail behind the Vacant / Already Rented tiles: a per-building summary, then
 * every counted unit, then the past-end leases the counts leave out. `kind` picks the
 * angle — "vacant" lists everything going vacant, "rented" only what is already let.
 */
const AvailabilityPopover = ({ kind, detail, stats, hasRentedInfo }) => {
  const { window: win } = detail;
  const rentedView = kind === "rented";
  const units = rentedView ? detail.units.filter((u) => u.rented) : detail.units;
  const pastEnd = rentedView ? detail.pastEnd.filter((u) => u.rented) : detail.pastEnd;

  return (
    <div className="avp" role="dialog" aria-label={rentedView ? "Already rented detail" : "Vacant detail"}>
      <div className="avp-head">
        <div className="avp-title">
          {rentedView ? `Already rented · ${stats.rented} of ${stats.goingVacant}` : `Vacant or going vacant by ${win.endLabel}`}
        </div>
        <div className="avp-sub">
          {stats.vacant} vacant now · {stats.expiring} lease{stats.expiring === 1 ? "" : "s"} ending by end of {win.monthLabel}
          {hasRentedInfo && ` · ${stats.rented} already rented · ${stats.goingVacant - stats.rented} still to rent`}
        </div>
        {hasRentedInfo && (stats.renewedExpiring > 0 || stats.renewalsPending > 0) && (
          <div className="avp-sub">
            Renewals on file: {stats.renewedExpiring} signed (counted as rented)
            {stats.renewalsPending > 0 && ` · ${stats.renewalsPending} awaiting signatures`}
          </div>
        )}
      </div>

      {detail.groups.length > 0 && (
        <table className="avp-table avp-table--summary">
          <thead>
            <tr>
              <th>Property</th>
              <th className="avp-num">Vacant</th>
              <th className="avp-num">Ending</th>
              {hasRentedInfo && <th className="avp-num">Rented</th>}
              {hasRentedInfo && <th className="avp-num">To rent</th>}
              {hasRentedInfo && <th className="avp-num">Renewals</th>}
            </tr>
          </thead>
          <tbody>
            {detail.groups.map((g) => {
              const out = g.vacant + g.ending;
              return (
                <tr key={g.key}>
                  <td>{place(g)}</td>
                  <td className="avp-num">{g.vacant || "—"}</td>
                  <td className="avp-num">{g.ending || "—"}</td>
                  {hasRentedInfo && <td className={`avp-num ${g.rented >= out ? "avp-up" : ""}`}>{g.rented} / {out}</td>}
                  {hasRentedInfo && <td className="avp-num">{out - g.rented || "—"}</td>}
                  {hasRentedInfo && (
                    <td className="avp-num">
                      {g.renewed || g.renewalsPending ? `${g.renewed}${g.renewalsPending ? ` + ${g.renewalsPending} unsigned` : ""}` : "—"}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      <div className="avp-section">{rentedView ? "Units with a signed lease waiting" : "Units"}</div>
      {units.length === 0 ? (
        <div className="avp-empty">{rentedView ? "No signed leases waiting yet." : `Nothing vacant or ending by ${win.endLabel}.`}</div>
      ) : (
        <table className="avp-table">
          <thead>
            <tr>
              <th>Unit</th>
              <th>Now</th>
              {rentedView ? (
                <>
                  <th>Incoming</th>
                  <th className="avp-num">New rent vs target</th>
                </>
              ) : (
                <>
                  <th className="avp-num">Target</th>
                  {hasRentedInfo && <th>Next resident</th>}
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {units.map((u) => (
              <tr key={u.key}>
                <td>
                  <span className="avp-strong">{u.unit_no}</span>
                  <div className="avp-muted avp-small">{place(u)}</div>
                </td>
                <td><Now u={u} /></td>
                {rentedView ? (
                  <>
                    <td><NextLease next={u.next} none={<Incoming lease={u.incoming} />} /></td>
                    <td className="avp-num"><RentVsTarget rent={u.incoming?.basic_rent} target={u.target_rent} /></td>
                  </>
                ) : (
                  <>
                    <td className="avp-num">{u.target_rent ? money(u.target_rent) : "—"}</td>
                    {hasRentedInfo && (
                      <td><NextLease next={u.next} /></td>
                    )}
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Lease ending, renewal already in the system (owner, 2026-09-30): the same resident
          stays — already rented once it is signed. */}
      {!rentedView && hasRentedInfo && detail.renewals.length > 0 && (
        <>
          <div className="avp-section avp-section--renewals">
            Renewals on file · {detail.renewals.length} unit{detail.renewals.length === 1 ? "" : "s"}
            <span className="avp-muted"> — lease ending, renewal already in the system</span>
          </div>
          <table className="avp-table">
            <thead>
              <tr>
                <th>Unit</th>
                <th>Resident</th>
                <th>Renewal term</th>
                <th className="avp-num">Rent now → renewal</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {detail.renewals.map((u) => (
                <tr key={`renewal-${u.key}`}>
                  <td>
                    <span className="avp-strong">{u.unit_no}</span>
                    <div className="avp-muted avp-small">{place(u)}</div>
                  </td>
                  <td>
                    <span className="avp-strong">{u.tenant}</span>
                    <div className="avp-muted avp-small">{u.where === "PAST_END" ? "ended" : "ends"} {day(u.lease_end)}</div>
                  </td>
                  <td><Term lease={u.next.lease} /></td>
                  <td className="avp-num">
                    {u.basic_rent ? money(u.basic_rent) : "—"} → <RentVsTarget rent={u.next.lease?.basic_rent} target={u.target_rent} />
                  </td>
                  <td>
                    {u.next.signed
                      ? <span className="avp-tag avp-tag--renewed">Signed · rented</span>
                      : <span className="avp-tag avp-tag--pending">Awaiting signatures</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {pastEnd.length > 0 && (
        <>
          <div className="avp-section avp-section--past">
            Past lease end · {pastEnd.length} unit{pastEnd.length === 1 ? "" : "s"}
            {!rentedView && hasRentedInfo && ` (${pastEnd.filter((u) => u.rented).length} with a signed lease waiting)`}
            <span className="avp-muted"> — not counted above; tenant still listed</span>
          </div>
          <table className="avp-table">
            <tbody>
              {pastEnd.map((u) => (
                <tr key={u.key}>
                  <td>
                    <span className="avp-strong">{u.unit_no}</span>
                    <div className="avp-muted avp-small">{place(u)}</div>
                  </td>
                  <td>
                    {u.tenant}
                    <span className="avp-down"> ended {day(u.lease_end)}</span>
                  </td>
                  {hasRentedInfo && <td><NextLease next={u.next} none={<span className="avp-muted">—</span>} /></td>}
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
};

export default AvailabilityPopover;
